import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { Language } from '../types/kundali';
import { useClickOutside } from '../hooks/useClickOutside';
import { SUPPORTED_LANGUAGES } from '../constants/languages';

interface LanguageSwitcherProps {
  lang: Language | string;
  setLang: (lang: Language) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  lang, 
  setLang, 
  className = '',
  showLabel = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Global Outside-Click & Escape Dismissal
  useClickOutside([dropdownRef, panelRef], isOpen, () => setIsOpen(false));

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 6,
        right: Math.max(8, window.innerWidth - rect.right)
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0];

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Switch Language / भाषा / भाषापरिवर्तनम्"
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-xl border border-astro-border bg-astro-bg hover:bg-astro-card text-astro-primary transition-colors cursor-pointer shadow-2xs"
      >
        <Globe className="w-3.5 h-3.5 text-astro-primary" />
        <span className="font-medium text-xs">{currentLang.native}</span>
        {showLabel && <span className="text-[10px] text-astro-muted">({currentLang.label})</span>}
        <ChevronDown className="w-3 h-3 text-astro-accent" />
      </button>

      {isOpen && coords && (
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: `${coords.top}px`,
            right: `${coords.right}px`,
            zIndex: 60
          }}
          className="w-64 max-h-80 overflow-y-auto bg-astro-bg border border-astro-border rounded-2xl shadow-xl py-2 animate-fade-in text-astro-text"
        >
          <div className="px-3 py-1 text-[10px] uppercase font-bold tracking-wider text-astro-muted border-b border-astro-border/70 mb-1">
            Pan-Indic Language Registry (13)
          </div>
          <div className="divide-y divide-astro-border/30">
            {SUPPORTED_LANGUAGES.map((item) => {
              const isSelected = item.code === lang;
              return (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => {
                    setLang(item.code as Language);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 flex items-center justify-between text-xs transition-colors cursor-pointer ${
                    isSelected ? 'bg-astro-card text-astro-primary font-bold' : 'hover:bg-[#FAF6ED] text-astro-text'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-xs">{item.native}</span>
                    <span className="text-[11px] text-astro-muted">({item.label})</span>
                  </div>
                  {isSelected && <Check className="w-3.5 h-3.5 text-astro-accent" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
